import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import {
  clickAnimated,
  clickFirstRowIn,
  clickThrough,
  delay,
  pulse,
  typeInto,
} from '@/utils/tour/actions';
import { logger } from '@/utils/logger';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for Contract Templates: templates hold one .docx
 * per variant, each variant's placeholders become editable fields, and the
 * drafting wizard turns them into a finished document with a live preview.
 * The demo navigates and fills ONE compose field so the live preview can
 * show itself; it never uploads, edits fields, or generates a document.
 * While a tour is active the wizard runs ephemerally — it creates no draft
 * on the backend and autosaves nothing — so the user's Drafts list is left
 * exactly as the tour found it.
 */

// Whether each hop of the walk actually happened — set synchronously by
// the open actions, so the scenes behind them can skip instantly when a
// no-data org has nothing to open instead of burning their route pushes
// and target timeouts one by one. Route checks cover mid-tour resumes.
let templateOpened = false;
let variantOpened = false;
let draftStarted = false;

/**
 * Templates open via the NAME link in their row (a plain `.link-button`) —
 * the row's first `.n-button` is the ⚡ quick-draft action, which would
 * jump straight into the wizard and derail the tour.
 */
async function openFirstTemplate(ctx: TourActionContext): Promise<void> {
  templateOpened = false;
  // The list is a GaiaDataTable now: rows are `.row` divs and the record link
  // is the `.name-link` button in the first track.
  templateOpened = await clickFirstRowIn(ctx, 'tpl.list', '.row .name-link');
}

const templateOpen = (): boolean =>
  templateOpened || getRouter().currentRoute.value.name === 'contract-generation-template-detail';

/** Variant cards are clickable as a whole — open the first one. */
async function openFirstVariant(ctx: TourActionContext): Promise<void> {
  variantOpened = false;
  const section = await waitForTourElement('tpl.variants', 3000);
  if (!section || ctx.isCancelled()) {
    return;
  }
  const card = await waitForCondition(
    () => section.querySelector<HTMLElement>('.variant-card'),
    3000
  );
  if (!card || ctx.isCancelled()) {
    logger.warn('[tour] template has no variants — skipping the variant hop');
    return;
  }
  card.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(card);
  if (ctx.animate) {
    await delay(700);
  }
  await clickAnimated(card, ctx);
  variantOpened = !ctx.isCancelled();
}

const variantOpen = (): boolean =>
  variantOpened ||
  getRouter().currentRoute.value.name === 'contract-generation-template-variant-detail' ||
  getRouter().currentRoute.value.name === 'contract-generation-template-author';

/**
 * Fill the FIRST fillable compose field, respecting its widget type:
 * strings are typed, numbers get a number, selections pick their first
 * option. Dates and booleans are stepped over (a panel dance or a state
 * flip demo poorly); if nothing fillable exists the fill skips and the
 * preview scene still explains itself.
 */
async function fillFirstField(ctx: TourActionContext): Promise<void> {
  const form = await waitForTourElement('tpl.compose-form', 3000);
  if (!form || ctx.isCancelled()) {
    return;
  }
  await waitForCondition(
    () => form.querySelector('.n-input, .n-select, .n-input-number, .n-date-picker'),
    3000
  );
  // Leaf widgets in form order. A bare .n-input also lives INSIDE number,
  // date, and select widgets — count it only when it is standalone text.
  // `.gaia-date-field` is a typed date: an n-input on the outside too, so it
  // has to be named here or the fill would type prose into a date.
  const widgets = [
    ...form.querySelectorAll<HTMLElement>(
      '.n-select, .n-input-number, .n-date-picker, .gaia-date-field, .n-input'
    ),
  ].filter(el => {
    if (el.getClientRects().length === 0) {
      return false;
    }
    return el.classList.contains('n-input')
      ? el.closest(
          '.n-select, .n-input-number, .n-date-picker, .gaia-date-field, .n-base-selection'
        ) === null
      : true;
  });

  for (const widget of widgets.slice(0, 8)) {
    if (ctx.isCancelled()) {
      return;
    }
    widget.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });

    if (widget.classList.contains('n-select')) {
      ctx.setHighlight(widget);
      if (ctx.animate) {
        await delay(600);
      }
      const trigger = widget.querySelector<HTMLElement>('.n-base-selection') ?? widget;
      await clickAnimated(trigger, ctx);
      const found = await waitForCondition(
        () => document.querySelector<HTMLElement>('.n-base-select-menu .n-base-select-option'),
        3000
      );
      if (!found || ctx.isCancelled()) {
        // Never leave the dropdown hanging open behind the scene.
        await clickAnimated(trigger, ctx);
        continue;
      }
      // The menu virtual-list re-renders its option nodes: ANY delay between
      // querying an option and clicking it (even clickAnimated's pulse beat)
      // can land the click on a detached node — selecting nothing and
      // leaving the menu stuck open (observed live). Query and click in the
      // same breath; verify the placeholder cleared, one retry for luck.
      for (let attempt = 0; attempt < 2; attempt++) {
        await delay(350);
        const option = document.querySelector<HTMLElement>(
          '.n-base-select-menu .n-base-select-option'
        );
        if (!option || ctx.isCancelled()) {
          break;
        }
        pulse(option);
        clickThrough(option);
        await delay(300);
        if (!widget.querySelector('.n-base-selection-placeholder')) {
          break;
        }
      }
    } else if (widget.classList.contains('n-input-number')) {
      const input = widget.querySelector<HTMLElement>('input');
      if (!input) {
        continue;
      }
      ctx.setHighlight(widget);
      if (ctx.animate) {
        await delay(600);
      }
      await typeInto(input, '12', ctx, { charMs: 90 });
    } else if (widget.classList.contains('n-input')) {
      ctx.setHighlight(widget);
      if (ctx.animate) {
        await delay(600);
      }
      await typeInto(widget, 'Acme Demo GmbH', ctx, { charMs: 45 });
    } else {
      // Date field/picker (or anything else): move on to the next field.
      continue;
    }

    if (ctx.animate) {
      // Let the preview re-render with the new value before the next bubble.
      await delay(1200);
    }
    return;
  }
  logger.warn('[tour] no fillable field found in the compose form — skipping the demo fill');
}

/**
 * Click the Draft button ourselves (instead of a declarative click) so the
 * wizard scenes' prerequisites know whether drafting actually started — a
 * route check alone would race the click's navigation.
 */
async function openDraftWizard(ctx: TourActionContext): Promise<void> {
  draftStarted = false;
  const button = await waitForTourElement('tpl.draft-button');
  if (!button || ctx.isCancelled()) {
    return;
  }
  button.scrollIntoView({ block: 'center', behavior: ctx.animate ? 'smooth' : 'auto' });
  ctx.setHighlight(button);
  if (ctx.animate) {
    await delay(550);
  }
  if (ctx.isCancelled()) {
    return;
  }
  await clickAnimated(button, ctx);
  draftStarted = !ctx.isCancelled();
}

const draftOpen = (): boolean =>
  draftStarted || getRouter().currentRoute.value.name === 'contract-generation-wizard';

/** Back through history — detail pages and the wizard stack on the list. */
async function goBackInHistory(): Promise<void> {
  getRouter().back();
  await delay(500);
}

/**
 * Entering the draft scene from the field editor: return to the variant
 * detail first — the Draft button lives there. The wizard it opens starts
 * blank on its own: `/new/:templateUid` is never bound to a stored draft.
 */
async function backToVariant(): Promise<void> {
  if (getRouter().currentRoute.value.name === 'contract-generation-template-author') {
    getRouter().back();
    await delay(500);
  }
}

export const templatingTour: TourDefinition = {
  key: 'templating',
  title: 'Contract Templates',
  description: 'From a .docx template to a finished document, live.',
  entryRoute: 'contract-generation-templates',
  requiredPermission: 'can_use_contract_generation',
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'contract-generation-templates' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'list',
      route: 'contract-generation-templates',
      target: 'tpl.list',
      placement: 'top',
      title: 'Your contract templates',
      body:
        'A template is a .docx with placeholders: write the contract once, ' +
        'generate it endlessly with new values.',
    },
    {
      id: 'create',
      target: 'tpl.create-button',
      placement: 'bottom-end',
      title: 'Bringing a template in',
      body:
        'Create template uploads your .docx; its placeholders are detected ' +
        'automatically. I will leave the uploading to you.',
    },
    {
      id: 'open',
      route: 'contract-generation-templates',
      target: 'tpl.list',
      placement: 'top',
      title: 'Inside a template',
      body: 'Let’s open one and look around.',
      actions: [{ kind: 'custom', run: openFirstTemplate }],
    },
    {
      id: 'variants',
      prerequisite: templateOpen,
      route: 'contract-generation-template-detail',
      target: 'tpl.variants',
      placement: 'top',
      title: 'One template, many variants',
      body:
        'Each variant carries its own .docx: the German wording, the ' +
        'freelancer edition, whatever you need. Let me open one.',
      actions: [{ kind: 'custom', run: openFirstVariant }],
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'fields',
      prerequisite: variantOpen,
      // Tolerates the variant-card click's navigation landing after playback
      // (param route: the engine never pushes it, the wander-off guard just
      // accepts arriving on it).
      route: 'contract-generation-template-variant-detail',
      target: 'tpl.fields',
      placement: 'left',
      title: 'The template’s fields',
      body: 'Every placeholder in the document becomes a field. Let me open ' + 'the field editor.',
      actions: [{ kind: 'click', target: 'tpl.edit-fields' }],
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'editor',
      prerequisite: variantOpen,
      route: 'contract-generation-template-author',
      target: 'tpl.editor',
      placement: 'right',
      title: 'Shape the fields',
      body:
        'This is where the template’s logic lives: rename fields, set ' +
        'their types and options, and wire them to the document. We will ' +
        'leave everything as it is.',
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'draft',
      onEnter: backToVariant,
      target: 'tpl.draft-button',
      placement: 'bottom-end',
      title: 'From template to document',
      // Needs an opened variant, and a variant still validating (or broken)
      // cannot draft — skip instead of pointing at a dead button. (The
      // disabled check stays absence-tolerant: the button only appears
      // after onEnter navigates back to the variant detail.)
      prerequisite: () =>
        variantOpen() &&
        !document.querySelector('[data-tour="tpl.draft-button"]')?.hasAttribute('disabled'),
      body:
        'Now, creating a document starts here. I will open the drafting ' +
        'wizard — it lands straight in the document.',
      actions: [{ kind: 'custom', run: openDraftWizard }],
    },
    {
      id: 'compose',
      prerequisite: draftOpen,
      // Same navigation-race tolerance for the Draft click's hop into the
      // wizard — without this the wander-off guard can end the tour the
      // moment the navigation commits.
      route: 'contract-generation-wizard',
      target: 'tpl.compose-form',
      placement: 'right',
      title: 'Fill the fields, watch the document',
      body:
        'No setup to get through: the template is picked, so the fields are ' +
        'already here. Every field on the left feeds the document on the ' +
        'right, and your work is saved as a draft as you go. I will fill ' +
        'the first one — keep an eye on the preview.',
      actions: [{ kind: 'custom', run: fillFirstField }],
      backActions: [{ kind: 'custom', run: goBackInHistory }],
    },
    {
      id: 'preview',
      prerequisite: draftOpen,
      target: 'tpl.preview',
      placement: 'left',
      title: 'The live preview',
      body:
        'There it is, straight in the document. Every value you type ' +
        'updates the preview instantly, so you always see the contract ' +
        'you are about to create.',
    },
    {
      id: 'generate',
      prerequisite: draftOpen,
      target: 'tpl.wizard-continue',
      placement: 'top-end',
      title: 'The finish line',
      body:
        'This files the document as a contract. When the template has signers, it ' +
        'first asks how to send it for signing: provider, signing order, message and expiry. ' +
        'The arrow beside it lets you just save instead. Nothing is created until then.',
    },
    {
      id: 'finale',
      title: 'That is Contract Templates',
      body:
        'Upload a .docx once, keep a variant per wording or language, and draft ' +
        'documents with a live preview. Real drafts are saved as you go ' +
        'and wait for you under Drafts; this demo left none behind.',
    },
  ],
};
